import gluedin from "gluedin-shorts-js";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
  createSdkCredentialFingerprint,
  createSdkCredentialSignature,
  getSdkEnvConfig,
  resolveSdkCredentials,
} from "../services/sdkCredentials";
import { syncSdkCredentialSignature } from "../services/sdkSession";
import { TemplateType } from "../types/template";

type UseSdkInitializationOptions = {
  pathname: string;
  selectedSdkTemplateType: TemplateType | null;
};

export const useSdkInitialization = ({
  pathname,
  selectedSdkTemplateType,
}: UseSdkInitializationOptions) => {
  const envConfig = useMemo(() => getSdkEnvConfig(), []);
  const lastSdkInitSignatureRef = useRef("");

  const sdkCredentials = useMemo(
    () => resolveSdkCredentials(selectedSdkTemplateType, envConfig),
    [envConfig, selectedSdkTemplateType]
  );

  const sdkCredentialSignature = useMemo(
    () =>
      createSdkCredentialSignature(envConfig.baseUrl, sdkCredentials),
    [envConfig.baseUrl, sdkCredentials]
  );
  const sdkCredentialFingerprint = useMemo(
    () => createSdkCredentialFingerprint(sdkCredentialSignature),
    [sdkCredentialSignature]
  );

  useLayoutEffect(() => {
    if (!envConfig.baseUrl || !sdkCredentials.apiKey || !sdkCredentials.secretKey) {
      return;
    }
    syncSdkCredentialSignature({
      rawSignature: sdkCredentialSignature,
      fingerprint: sdkCredentialFingerprint,
    });

    if (lastSdkInitSignatureRef.current === sdkCredentialSignature) {
      return;
    }

    const gluedinSDKInitilize = new gluedin.GluedInSDKInitilize();
    gluedinSDKInitilize.initialize({
      baseUrl: envConfig.baseUrl,
      apiKey: sdkCredentials.apiKey,
      secretKey: sdkCredentials.secretKey,
    });

    lastSdkInitSignatureRef.current = sdkCredentialSignature;
  }, [
    envConfig.baseUrl,
    sdkCredentialSignature,
    sdkCredentialFingerprint,
    sdkCredentials.apiKey,
    sdkCredentials.secretKey,
  ]);

  useLayoutEffect(() => {
    if (pathname.includes("discover")) {
      localStorage.setItem("contentRoute", "discover");
    } else {
      localStorage.removeItem("contentRoute");
    }
  }, [pathname]);
};
